import { useEffect, useRef, useState } from 'react';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      return;
    }
    setStatus('submitting');
    const formData = new URLSearchParams();
    const formElements = form.elements;
    for (let i = 0; i < formElements.length; i++) {
      const el = formElements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) {
        if (el.tagName === 'SELECT') {
          formData.append(el.name, (el as HTMLSelectElement).options[(el as HTMLSelectElement).selectedIndex].text);
        } else {
          formData.append(el.name, el.value);
        }
      }
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d6u4nun6t0doov8ffo20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-24 md:py-32 relative"
      style={{
        background: 'linear-gradient(135deg, #1a3828 0%, #2d5a3d 50%, #1a3828 100%)',
      }}
    >
      <div className="absolute inset-0 ainu-pattern opacity-20" />
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-14 section-reveal">
          <span
            className="text-kura-gold text-xs tracking-[0.4em] uppercase"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            Contact
          </span>
          <h2
            className="text-white text-3xl md:text-4xl font-bold mt-2 tracking-wide"
            style={{ fontFamily: 'Noto Serif JP, serif' }}
          >
            お問い合わせ
          </h2>
          <div className="ainu-border mt-4 max-w-xs mx-auto" />
          <p
            className="text-white/70 text-sm mt-4 tracking-wide"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            査定・買取のご依頼・ご質問はお気軽にどうぞ。お返事は通常2営業日以内にいたします。
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-10 items-start">
          {/* Info card */}
          <div className="md:col-span-2 flex flex-col gap-5 section-reveal">
            <div className="bg-kura-green/50 backdrop-blur-sm rounded-lg p-6 border border-white/10">
              <h3
                className="text-kura-gold-light font-semibold mb-4 tracking-wide"
                style={{ fontFamily: 'Noto Serif JP, serif' }}
              >
                会社情報
              </h3>
              <ul className="flex flex-col gap-4">
                {[
                  { icon: 'ri-building-2-line', label: '会社名', value: '蔵合同会社' },
                  { icon: 'ri-store-line', label: '屋号', value: '買取こんかね' },
                  { icon: 'ri-map-pin-line', label: '営業エリア', value: '北海道を中心に対応（遠方相談可）' },
                  { icon: 'ri-home-office-line', label: '実店舗', value: '実店舗なし・催事出張買取専門' },
                ].map((info) => (
                  <li key={info.label} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className={`${info.icon} text-kura-gold text-lg`} />
                    </div>
                    <div>
                      <p
                        className="text-white/50 text-xs"
                        style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                      >
                        {info.label}
                      </p>
                      <p
                        className="text-white/90 text-sm"
                        style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                      >
                        {info.value}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-kura-gold/10 backdrop-blur-sm rounded-lg p-5 border border-kura-gold/30">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-star-line text-kura-gold text-lg" />
                </div>
                <p
                  className="text-white/75 text-xs leading-relaxed tracking-wide"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  査定・出張は完全無料です。
                  お断りいただいても費用は一切かかりません。
                  お気軽にご相談ください。
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-3 section-reveal">
            {status === 'success' ? (
              <div className="bg-kura-green-mid/60 rounded-lg p-10 text-center border border-kura-gold/30">
                <div className="w-16 h-16 flex items-center justify-center bg-kura-gold/20 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-kura-gold text-3xl" />
                </div>
                <h3
                  className="text-kura-gold-light text-xl font-semibold mb-2"
                  style={{ fontFamily: 'Noto Serif JP, serif' }}
                >
                  お問い合わせを受け付けました
                </h3>
                <p
                  className="text-white/70 text-sm leading-relaxed"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  2営業日以内にご連絡いたします。
                  <br />
                  しばらくお待ちください。
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 border border-kura-gold/50 text-kura-gold-light px-6 py-2 rounded-md text-sm cursor-pointer whitespace-nowrap hover:bg-kura-gold/10 transition-colors duration-200"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  フォームに戻る
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-kura-green/40 backdrop-blur-sm rounded-lg p-6 md:p-8 border border-white/10 flex flex-col gap-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      className="block text-white/70 text-xs mb-1.5 tracking-wide"
                      style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                    >
                      お名前 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="山田 太郎"
                      className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-kura-gold transition-colors duration-200"
                      style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-white/70 text-xs mb-1.5 tracking-wide"
                      style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                    >
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="090-xxxx-xxxx"
                      className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-kura-gold transition-colors duration-200"
                      style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="block text-white/70 text-xs mb-1.5 tracking-wide"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    メールアドレス <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="example@email.com"
                    className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-kura-gold transition-colors duration-200"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  />
                </div>

                <div>
                  <label
                    className="block text-white/70 text-xs mb-1.5 tracking-wide"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    お問い合わせ種別
                  </label>
                  <select
                    name="inquiry_type"
                    className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-kura-gold transition-colors duration-200"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    <option value="買取依頼" className="text-kura-dark">買取依頼・出張査定</option>
                    <option value="催事出張の相談" className="text-kura-dark">催事出張の相談</option>
                    <option value="品目の確認" className="text-kura-dark">取扱品目の確認</option>
                    <option value="その他" className="text-kura-dark">その他のお問い合わせ</option>
                  </select>
                </div>

                <div>
                  <label
                    className="block text-white/70 text-xs mb-1.5 tracking-wide"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    お問い合わせ内容 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="お品物の種類・点数・査定希望の日程など、お気軽にお書きください。"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-kura-gold transition-colors duration-200 resize-none"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  />
                  <div className="text-right mt-1">
                    <span
                      className={`text-xs ${charCount > 480 ? 'text-red-400' : 'text-white/40'}`}
                      style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                    >
                      {charCount}/500
                    </span>
                  </div>
                </div>

                {status === 'error' && (
                  <p
                    className="text-red-400 text-xs text-center"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    送信に失敗しました。お手数ですが再度お試しください。
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting' || charCount > 500}
                  className="bg-kura-gold hover:bg-kura-gold-light text-kura-dark font-semibold py-3 rounded-md text-sm tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                >
                  {status === 'submitting' ? '送信中...' : '無料査定を申し込む'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
